<template>
  <div class="card">
    <div class="card-header">
      <h2>{{ contentListTableInfo.title }}</h2>
    </div>
    <div class="card-subtitle">{{ contentListTableInfo.subTitle }}</div>
    <div class="search-container">
      <input
        type="text"
        v-model="searchQuery"
        placeholder="Search..."
        class="search-input"
      />
    </div>
    <div class="table-responsive">
      <table class="modern-table" v-if="!contentLoading">
        <thead>
          <tr>
            <th
              v-for="column in contentListTableInfo.columns"
              :key="column.value"
            >
              {{ column.text }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="content in filteredContent" :key="content.id">
            <td :title="content.userName">
              {{ truncateText(content.userName, 30) }}
            </td>
            <td :title="content.phoneNo">
              {{ truncateText(content.phoneNo, 30) }}
            </td>
            <td :title="content.mail">{{ truncateText(content.mail, 30) }}</td>
            <td :title="content.course">
              {{ truncateText(content.course, 30) }}
            </td>
            <td :title="content.description">
              {{ truncateText(content.description, 30) }}
            </td>
            <td>{{ formatDate(content.createdDate) }}</td>
          </tr>
        </tbody>
      </table>
      <div v-else class="loading-indicator">
        <div class="spinner"></div>
      </div>
    </div>
    <div class="pagination">
      <button @click="prevPage" :disabled="currentPage === 1">Previous</button>
      <span>Page {{ currentPage }} of {{ totalPages }}</span>
      <button @click="nextPage" :disabled="currentPage === totalPages">
        Next
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed } from "vue";
import dayjs from "dayjs";
import axios from "axios";

import { getBaseUrl } from "../helper/constant.js";

// Content
const contentLoading = ref(false);
const contentList = ref([]);
const contentListTableInfo = ref({
  title: "Registration Table",
  subTitle: "",
  columns: [
    { text: "User Name", value: "userName" },
    { text: "Phone Number", value: "phoneNo" },
    { text: "Mail", value: "mail" },
    { text: "Course", value: "course" },
    { text: "Description", value: "description" },
    { text: "Created Date", value: "createdDate" },
  ],
  data: contentList.value,
});

// Search
const searchQuery = ref("");

// Pagination
const currentPage = ref(1);
const pageSize = ref(10);
const totalItems = ref(0);

const totalPages = computed(() => Math.ceil(totalItems.value / pageSize.value));

const filteredContent = computed(() => {
  if (!searchQuery.value) {
    return contentList.value;
  }
  return contentList.value.filter((content) =>
    Object.values(content).some((val) =>
      String(val).toLowerCase().includes(searchQuery.value.toLowerCase()),
    ),
  );
});
async function getContent(skip = 0, take = 10) {
  if (contentLoading.value) return;
  contentLoading.value = true;
  contentList.value = [];

  try {
    const { data } = await axios.get(getBaseUrl() + `/core/registration`, {
      params: { skip, take },
    });
    contentList.value = data.list;
    totalItems.value = data.count;
  } catch (er) {
    console.log(er);
  } finally {
    contentLoading.value = false;
  }
}
const formatDate = (dateString) => {
  return dayjs(dateString).format("YYYY.MM.DD HH:mm");
};
function truncateText(text, length) {
  if (!text || !length) return "";
  if (text.length <= length) {
    return text;
  }
  return text.substring(0, length) + "...";
}
// Pagination methods
const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
    getContent((currentPage.value - 1) * pageSize.value, pageSize.value);
  }
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
    getContent((currentPage.value - 1) * pageSize.value, pageSize.value);
  }
};

watch(contentList, (newValue) => {
  contentListTableInfo.value.data = newValue;
});

onMounted(() => {
  getContent();
});
</script>

<style scoped>
.selectedInitialContent {
  background-color: #ccffd1 !important;
}
.selectedReviewContent {
  background-color: #cce5ff !important;
}
.selectedDeletionContent {
  background-color: #f8d7da !important;
}
.icon-button {
  background: none;
  border: none;
  cursor: pointer;
  margin-right: 5px;
}
.icon-button i {
  font-size: 16px;
  color: #333;
}
.card {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 16px;
  margin: 16px 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.card-header h2 {
  margin: 0;
}
.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  background-color: #007bff;
  color: white;
  cursor: pointer;
}
.btn-primary {
  background-color: #007bff;
}
.table-responsive {
  overflow-x: auto;
}
.modern-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 16px;
}
.modern-table th,
.modern-table td {
  padding: 12px;
  border: 1px solid #ddd;
  text-align: left;
}
.modern-table th {
  background-color: #f8f8f8;
}
.modern-table tr:nth-child(even) {
  background-color: #f2f2f2;
}
.loading-indicator {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
}
.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-top: 4px solid #007bff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 16px;
}
.pagination button {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  background-color: #007bff;
  color: white;
  cursor: pointer;
  margin: 0 8px;
}
.pagination span {
  margin: 0 8px;
}
.search-container {
  margin-top: 5px;
  margin-bottom: 16px;
}
.search-input {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}
</style>
